import "./TradingBackground.css";

/**
 * Fixed animated trading-themed background painted behind everything.
 * Pure CSS + SVG — GPU-friendly, no images.
 *
 * Layers:
 *   1. Grid (faint)
 *   2. Long chart line drifting slowly to the left
 *   3. Distant candlestick pattern
 *   4. 3 glow orbs (green/red/gold) drifting
 *   5. Ticker-tape strip along the bottom with fake prices
 */

const TICKERS = [
  { s: "EUR/USD", p: "1.0847", d: 1 },
  { s: "GBP/USD", p: "1.2712", d: -1 },
  { s: "USD/JPY", p: "154.28", d: 1 },
  { s: "BTC/USDT", p: "68,412", d: 1 },
  { s: "ETH/USDT", p: "3,542", d: -1 },
  { s: "XAU/USD", p: "2,317", d: 1 },
  { s: "USD/CHF", p: "0.9124", d: -1 },
  { s: "AUD/USD", p: "0.6612", d: 1 },
  { s: "SOL/USDT", p: "182.45", d: 1 },
  { s: "USD/CAD", p: "1.3651", d: -1 },
];

// Generate a smooth line path using deterministic pseudo-random offsets
function makeChartPath(width = 2400, height = 240, points = 60) {
  const stepX = width / points;
  let d = "";
  let y = height / 2;
  for (let i = 0; i <= points; i++) {
    const x = i * stepX;
    y += (Math.sin(i * 0.55) + Math.cos(i * 0.31)) * 12 + (((i * 9301) % 233) - 116) * 0.05;
    y = Math.max(20, Math.min(height - 20, y));
    d += (i === 0 ? "M " : " L ") + x.toFixed(1) + " " + y.toFixed(1);
  }
  return d;
}

const CHART_PATH = makeChartPath();

// Generate candlesticks in a scattered grid pattern
function makeCandles(cols = 40, rows = 8, cellW = 60, cellH = 90) {
  const arr = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const seed = r * cols + c;
      // pseudo-random using seed
      const rand = (x) => ((Math.sin(x) * 43758.5453) % 1 + 1) % 1;
      const bull = rand(seed + 1) > 0.5;
      const cx = c * cellW + cellW / 2;
      const cy = r * cellH + cellH / 2 + rand(seed + 2) * 20 - 10;
      const bodyH = 20 + rand(seed + 3) * 32;
      const wickH = bodyH + 10 + rand(seed + 4) * 20;
      arr.push({ cx, cy, bodyH, wickH, bull, key: seed });
    }
  }
  return arr;
}

const CANDLES = makeCandles();

export default function TradingBackground() {
  return (
    <div className="trading-bg" aria-hidden="true">
      <div className="grid" />

      {/* Distant candlesticks */}
      <div className="candles">
        <svg viewBox="0 0 2400 720" preserveAspectRatio="xMidYMid slice">
          {CANDLES.map(({ cx, cy, bodyH, wickH, bull, key }) => (
            <g key={key} stroke={bull ? "#10b981" : "#ef4444"} strokeWidth="1.5">
              <line x1={cx} x2={cx} y1={cy - wickH / 2} y2={cy + wickH / 2} />
              <rect
                x={cx - 5}
                y={cy - bodyH / 2}
                width="10"
                height={bodyH}
                fill={bull ? "#10b981" : "#ef4444"}
                opacity="0.55"
              />
            </g>
          ))}
        </svg>
      </div>

      {/* Chart line */}
      <div className="chart">
        <svg viewBox="0 0 4800 240" preserveAspectRatio="none">
          <defs>
            <linearGradient id="chartGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.2" />
              <stop offset="50%" stopColor="#34d399" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0.2" />
            </linearGradient>
            <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.15" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* duplicate the path so drifting loops seamlessly */}
          <g>
            <path d={CHART_PATH} fill="none" stroke="url(#chartGrad)" strokeWidth="1.8" />
            <path d={CHART_PATH + " L 2400 240 L 0 240 Z"} fill="url(#chartFill)" stroke="none" />
          </g>
          <g transform="translate(2400 0)">
            <path d={CHART_PATH} fill="none" stroke="url(#chartGrad)" strokeWidth="1.8" />
            <path d={CHART_PATH + " L 2400 240 L 0 240 Z"} fill="url(#chartFill)" stroke="none" />
          </g>
        </svg>
      </div>

      {/* Glow orbs */}
      <div className="orb buy" />
      <div className="orb sell" />
      <div className="orb gold" />

      {/* Ticker tape */}
      <div className="ticker" dir="ltr">
        {[...TICKERS, ...TICKERS].map((it, i) => (
          <span key={i}>
            <span className="sym">{it.s}</span>
            <span className={it.d > 0 ? "up" : "down"}>{it.p}</span>
            <span className={"arr " + (it.d > 0 ? "up" : "down")}>{it.d > 0 ? "▲" : "▼"}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
